import * as TableauUI from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { ButtonAttrs } from '../../utils/HtmlAttrs';
import { InteractiveDomState, InteractiveDomWrapper } from '../../utils/InteractiveDom';
import { EventUtilsOptOut } from '../../utils/ReactHelpers';
import { ActivitySpinnerSize, ActivitySpinnerWidget } from '../ActivitySpinnerWidget';
import { ButtonProps, ButtonType } from './ButtonWidget';

export { ButtonType, ButtonProps };

type ButtonKind = NonNullable<TableauUI.ButtonProps['kind']>;

// tslint:disable:deprecation
const ButtonKindMap: { [key in ButtonType]: ButtonKind } = Object.freeze({
  [ButtonType.Destructive]: 'destructive',
  [ButtonType.Attention]: 'destructive',
  [ButtonType.Grey]: 'outline',
  [ButtonType.Primary]: 'primary',
  [ButtonType.Go]: 'primary',
  [ButtonType.Outline]: 'outline',
  [ButtonType.OutlineDark]: 'outlineDark',
  [ButtonType.LightOutline]: 'outlineDark',
  [ButtonType.OutlineGo]: 'primary',
  [ButtonType.OutlineAttention]: 'destructive',
  [ButtonType.Text]: 'lowEmphasis',
  [ButtonType.DarkText]: 'lowEmphasis',
  [ButtonType.DarkBoldText]: 'lowEmphasis',
  [ButtonType.Icon]: 'outline',
  [ButtonType.IconOutline]: 'outline',
});
// tslint:enable:deprecation

// Once Icon buttons are first-class button kinds in Tableau UI, can delete from here...
const IconButtonStyle: React.CSSProperties = Object.freeze({
  alignItems: 'center',
  borderColor: 'transparent',
  boxSizing: 'border-box',
  color: TabStyles.Typography.Color.PrimaryLight,
  display: 'inline-flex',
  fontWeight: 'normal',
  height: 'auto',
  justifyContent: 'center',
  minHeight: 20,
  minWidth: 20,
  padding: 0,
  whiteSpace: 'nowrap',
});

const ButtonStyleOverrides: { [key in ButtonType]?: string } = Object.freeze({
  [ButtonType.Icon]: TypeStyle.style({
    ...IconButtonStyle,
    $nest: {
      '&:hover:enabled, &:focus': { backgroundColor: TabStyles.Colors.F3, color: TabStyles.Typography.Color.Primary },
      '&&&:active': { backgroundColor: TabStyles.Colors.F5, color: TabStyles.Typography.Color.Primary },
      '&:disabled': { borderColor: 'transparent !important', opacity: TabStyles.Opacity.Disabled },
    }
  }),
  [ButtonType.IconOutline]: TypeStyle.style({
    ...IconButtonStyle,
    border: 'none',
    position: 'relative',
    $nest: {
      '&:hover:enabled, &:focus, &&&:active': { backgroundColor: 'transparent' },
      '&:focus::after': {
        content: `""`,
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        right: 0,
        border: `1px solid ${TabStyles.Colors.Focus}`,
        borderRadius: 2,
        pointerEvents: 'none',
      },
      '&:disabled': { borderColor: 'transparent !important', opacity: TabStyles.Opacity.Disabled },
    }
  }),
});
// ...to here

const ButtonShowSpinnerStyleName = TypeStyle.style({
  alignItems: 'center',
  display: 'inline-flex',
  justifyContent: 'center',
  pointerEvents: 'none',
});

const DarkButtonKinds = Object.freeze<ButtonKind>(['primary', 'destructive', 'outlineDark']);
const ButtonSpinner: React.FC<{ buttonKind: ButtonKind, testId: string }> = props =>
  <ActivitySpinnerWidget
    onDarkBackground={DarkButtonKinds.indexOf(props.buttonKind) > -1}
    spinnerSize={ActivitySpinnerSize.Small}
    style={{ lineHeight: 0.5, position: 'absolute' }}
    testId={props.testId}
  />;

export class ButtonWidget extends React.Component<ButtonProps & ButtonAttrs, InteractiveDomState> {
  public static defaultProps: Pick<ButtonProps, 'buttonType' | 'density'> = { buttonType: ButtonType.Outline, density: 'low' };
  public static getDerivedStateFromProps(props: ButtonWidget['props']): InteractiveDomState | null {
    if (props.disabled) {
      return { active: false, focus: false, hover: false };
    }
    return null;
  }
  public state: InteractiveDomState = { active: false, focus: false, hover: false };

  private handleActive = (active: boolean): void => this.setState({ active });
  private handleFocus = (focus: boolean): void => this.setState({ focus });
  private handleHover = (hover: boolean): void => this.setState({ hover });
  // tslint:disable:deprecation
  private handleClick: React.MouseEventHandler<HTMLButtonElement & HTMLDivElement> = e => {
    if (this.props.disabled || this.props.showSpinner) {
      return;
    } else if (this.props.handleClick) {
      this.props.handleClick(e);
    } else if (this.props.onClick) {
      this.props.onClick(e);
    }
  }
  // tslint:enable:deprecation

  public render(): JSX.Element {
    // tslint:disable-next-line:max-line-length deprecation
    const { 'aria-disabled': ariaDisabled, buttonRef, buttonType, className, disabled, disableTabAccessibility, getContent, handleClick, role, selected, showSpinner, tabIndex, testId, ...rest } = this.props;

    const kind = ButtonKindMap[buttonType!];
    const buttonContent = getContent?.({ ...this.state, disabled, selected }) ?? rest.children;
    const children = !showSpinner ?
      buttonContent :
      <>
        {showSpinner && <ButtonSpinner testId={testId!} buttonKind={kind} />}
        <div style={{ visibility: 'hidden' }} children={buttonContent} />
      </>;

    const button =
      <TableauUI.Button
        {...EventUtilsOptOut}
        aria-checked={role === 'radio' ? selected : undefined}
        aria-disabled={showSpinner || ariaDisabled}
        aria-pressed={role === 'radio' ? undefined : selected}
        data-tb-test-id={makeTestId(testId!, 'Button')}
        {...rest}
        children={children}
        className={TypeStyle.classes(ButtonStyleOverrides[buttonType!], showSpinner && ButtonShowSpinnerStyleName, className)}
        disabled={disabled}
        kind={kind}
        onClick={this.handleClick}
        ref={buttonRef}
        role={role}
        tabIndex={disabled ? undefined : (this.props.disableTabAccessibility ? -1 : (tabIndex || 0))}
      />;

    if (getContent) {
      return <InteractiveDomWrapper
        children={button}
        disabled={disabled}
        handleActive={this.handleActive}
        handleFocus={this.handleFocus}
        handleHover={this.handleHover}
      />;
    } else {
      return button;
    }
  }
}
